import { Component, OnInit, ViewChild, ElementRef, AfterViewChecked } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { EntityService } from '../../@core/utils/entity.service';
import { environment } from '../../../environments/environment';

type NbChatMessage = {
  text: string;
  reply: boolean;     // true = message on right (user)
  sender: string;
  date?: Date;
  avatar?: string;
};

@Component({
  selector: 'ngx-chat-bot',
  templateUrl: './chat-bot.component.html',
  styleUrls: ['./chat-bot.component.scss']
})
export class ChatBotComponent implements OnInit, AfterViewChecked {

  @ViewChild('messagesContainer') private messagesContainer: ElementRef;
  messages: NbChatMessage[] = [
    {
      text: "Ask me anything about company policies and labour codes.",
      reply: false,
      sender: "MIRA",
      date: new Date(),
      avatar: environment.FE_URL + '/assets/images/mascot/MIRA%20HR%20Mascot-01.png',
    },
  ];

  loading = false;
  entityData;

  constructor(
    private entityDataService: EntityService,
    private sanitizer: DomSanitizer,
  ) {}

  ngOnInit(): void {
    this.entityData = this.entityDataService.getEntityDataByName('internal-doc');
  }

  ngAfterViewChecked(): void {
    this.scrollToBottom();
  }

  private scrollToBottom(): void {
    if (this.messagesContainer) {
      const el = this.messagesContainer.nativeElement;
      el.scrollTop = el.scrollHeight;
    }
  }

  /**
   * Convert UI messages to API format
   * @returns Last 20 messages in { role, content } format
   */
  private getConversationHistory() {
    // Take last 20 messages (excluding typing placeholders)
    const relevantMessages = this.messages
      .filter(m => m.text !== '…') // exclude typing indicators
      .slice(-20);

    return relevantMessages.map(m => ({
        role: m.reply ? 'user' : 'assistant',
        content: m.text
      }));
  }

  private extractResponseText(response: any): string {
    if (typeof response === 'string') {
      return response.trim();
    }

    if (response && typeof response.answer === 'string') {
      return response.answer.trim();
    }

    return '';
  }

  toHtml(text: string): SafeHtml {
    let html = text
      // Escape HTML entities first
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      // Headings
      .replace(/^### (.+)$/gm, '<h6 class="md-h">$1</h6>')
      .replace(/^## (.+)$/gm, '<h5 class="md-h">$1</h5>')
      .replace(/^# (.+)$/gm, '<h4 class="md-h">$1</h4>')
      // Horizontal rule
      .replace(/^---$/gm, '<hr class="md-hr">')
      // Bold + italic
      .replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>')
      // Bold
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/__(.+?)__/g, '<strong>$1</strong>')
      // Italic
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      .replace(/_(.+?)_/g, '<em>$1</em>')
      // Inline code
      .replace(/`([^`]+)`/g, '<code class="md-code">$1</code>');

    // Unordered lists — collect consecutive bullet lines into <ul>
    html = html.replace(/((?:^[ \t]*[-*] .+\n?)+)/gm, (block) => {
      const items = block.trim().split('\n')
        .map(l => l.replace(/^[ \t]*[-*] /, '').trim())
        .filter(Boolean)
        .map(l => `<li>${l}</li>`)
        .join('');
      return `<ul class="md-list">${items}</ul>`;
    });

    // Ordered lists
    html = html.replace(/((?:^[ \t]*\d+\. .+\n?)+)/gm, (block) => {
      const items = block.trim().split('\n')
        .map(l => l.replace(/^[ \t]*\d+\. /, '').trim())
        .filter(Boolean)
        .map(l => `<li>${l}</li>`)
        .join('');
      return `<ol class="md-list">${items}</ol>`;
    });

    // Paragraphs — split on blank lines, wrap non-block lines
    html = html.split(/\n{2,}/).map(chunk => {
      chunk = chunk.trim();
      if (!chunk) return '';
      if (/^<(ul|ol|h[1-6]|hr)/.test(chunk)) return chunk;
      // Single newlines within a paragraph become <br>
      return `<p class="md-p">${chunk.replace(/\n/g, '<br>')}</p>`;
    }).join('');

    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  onSend(event: any) {
    const userText = (event?.message || '').trim();
    if (!userText || this.loading) return;

    // Add user message to UI
    this.messages = [
      ...this.messages,
      { text: userText, reply: true, sender: 'You', date: new Date() },
    ];

    this.loading = true;

    // Add typing placeholder
    const typingMessage: NbChatMessage = {
      text: '…',
      reply: false,
      sender: 'MIRA',
      date: new Date(),
      avatar: environment.FE_URL + '/assets/images/mascot/MIRA%20HR%20Mascot-01.png',
    };
    this.messages = [...this.messages, typingMessage];

    // Get conversation history in API format
    const conversationPayload = this.getConversationHistory();

    // Send conversation history to API
    this.entityData.chat(conversationPayload).subscribe({
      next: (response) => {
        const responseText = this.extractResponseText(response) || "I couldn't find that in the documents.";
        
        // Replace typing placeholder with actual answer
        this.messages = this.messages.map(m =>
          m === typingMessage
            ? { ...m, text: responseText }
            : m
        );
        this.loading = false;
      },
      error: (err) => {
        this.messages = this.messages.map(m =>
          m === typingMessage
            ? { ...m, text: "Something went wrong. Try again in a moment." }
            : m
        );
        this.loading = false;
        console.error(err);
      },
    });
  }
}
